import { FileWarning, RefreshCw } from 'lucide-react';
import type { ReactElement } from 'react';

import { NoAccessPanel } from '@/components/ui/NoAccessPanel';
import { Pagination } from '@/components/ui/Pagination';
import { InlineError, StatePanel } from '@/components/ui/StatePanel';

import { visibleDefs, type ColumnId } from '../columns';
import type { CustomFieldColumn, MasterListRow } from '../types';
import { DocumentTable } from './DocumentTable';
import { TableSkeleton } from './TableSkeleton';
import { errorAdvice, refreshAdvice } from '@/lib/error-advice';

interface Props {
  isLoading: boolean;
  isError: boolean;
  /** La falla de la consulta: decide qué se le aconseja al usuario. */
  error: unknown;
  /** Hay datos de una carga anterior (una recarga falló, p. ej. sin conexión). */
  hasData: boolean;
  /** 403: sin acceso (rol sin lectura o acceso temporal vencido); reintentar no sirve. */
  forbidden: boolean;
  rows: MasterListRow[];
  /** Columnas elegidas (las del mockup más las opcionales del selector). */
  columns: readonly ColumnId[];
  /** Campos propios elegidos de la empresa activa (QA F1 #14). */
  customColumns: CustomFieldColumn[];
  page: number;
  pageSize: number;
  total: number;
  busy: boolean;
  onPage: (page: number) => void;
  onRetry: () => void;
  /** Estado sin filas (QA F1 #9). */
  empty: ReactElement;
}

/** Cuerpo de la Lista Maestra: esqueleto, sin acceso, error, vacío o la tabla. */
export function MasterListBody({
  isLoading,
  isError,
  error,
  hasData,
  forbidden,
  rows,
  columns,
  customColumns,
  page,
  pageSize,
  total,
  busy,
  onPage,
  onRetry,
  empty,
}: Props): ReactElement {
  if (isLoading) {
    return (
      <TableSkeleton
        columns={[
          ...visibleDefs(columns).map((c) => c.label),
          ...customColumns.map((c) => c.label),
          '',
        ]}
      />
    );
  }
  if (isError && forbidden) {
    return (
      <NoAccessPanel title="No tienes acceso a la Lista Maestra" className="d2">
        Tu rol no permite consultar documentos o tu acceso temporal venció.
      </NoAccessPanel>
    );
  }
  if (isError && !hasData) {
    return (
      <StatePanel
        icon={<FileWarning />}
        title="No se pudo cargar la lista maestra"
        tone="danger"
        role="alert"
        onRetry={onRetry}
        className="d2"
      >
        {errorAdvice(error)}
      </StatePanel>
    );
  }
  if (rows.length === 0) {
    return empty;
  }
  return (
    <div className="card rise d2">
      {/* Una recarga que falla (p. ej. sin conexión) no reemplaza lo que ya se veía:
          se avisa arriba de la tabla (Lente B del lote). */}
      {isError && (
        <InlineError icon={<RefreshCw size={16} />} onRetry={onRetry}>
          No se pudo actualizar la lista. Se muestran los datos anteriores.{refreshAdvice(error)}
        </InlineError>
      )}
      {/* Solo la tabla hace scroll horizontal en móvil; la paginación queda
          fija al ancho de la tarjeta (no se arrastra con el scroll). */}
      <div className="t-scroll">
        <DocumentTable rows={rows} columns={columns} customColumns={customColumns} />
      </div>
      <Pagination page={page} pageSize={pageSize} total={total} busy={busy} onPage={onPage} />
    </div>
  );
}
